<template>
  <div>
    <Card pt:content:class="p-0!" pt:body:class="p-2!">
      <template #content>
        <Card pt:content:class="p-0!" pt:body:class="p-6!" class="mb-2">
          <template #content>
            <div class="flex flex-row font-bold text-3xl 2xl:text-5xl justify-evenly">
              <div class="flex flex-col text-center my-auto">
                <p class="text-2xl">Original Principal</p>
                <p v-if="units.length > 0">{{ formatCurrency(principalPer) }}</p>
                <p v-else>No Units</p>
              </div>
              <div class="flex flex-col">
                <div class="flex flex-col text-center my-auto">
                  <p class="text-2xl">Unit Cost</p>
                  <p v-if="units.length > 0">{{ formatCurrency(props.project.projectDrawInfo.budget / units.length) }}</p>
                  <p v-else>No Units</p>
                </div>
                <div class="flex flex-col text-center my-auto">
                  <p class="text-2xl">Unit Sale Price</p>
                  <p v-if="salesPrice != 0">
                    {{ formatCurrency(salesPrice / units.length) }}
                  </p>
                  <p v-else-if="units.length == 0">No Units</p>
                  <p v-else>No Price Set</p>
                </div>
              </div>

              <Card class="mt-3 w-105 text-2xl" pt:body:class="h-full" pt:content:class="h-full">
                <template #content>
                  <div class="flex flex-col gap-2 justify-between h-full">
                    <div class="flex flex-col gap-1">
                      <label class="font-bold">Commitment</label>
                      <InputNumber v-model="commitment" mode="currency" currency="USD" :allow-empty="false"></InputNumber>
                    </div>
                    <div class="flex justify-between">
                      <div class="flex flex-col gap-1">
                        <p>Commited</p>
                        <span class="font-bold text-xl">{{ formatCurrency(totalCommited) }}</span>
                      </div>
                      <div class="flex flex-col gap-1">
                        <p>Available</p>
                        <span class="font-bold text-xl" :class="{ 'text-red-500': totalAvailable < 0 }">{{ formatCurrency(totalAvailable) }}</span>
                      </div>
                    </div>
                  </div>
                </template>
              </Card>
              <Card class="mt-3 w-105 text-2xl" pt:body:class="h-full" pt:content:class="h-full">
                <template #content>
                  <div class="flex flex-col gap-2 justify-between h-full">
                    <div class="flex flex-col gap-1">
                      <label class="font-bold">Available Balance</label>
                      <InputNumber v-model="availableBalance" mode="currency" currency="USD" :allow-empty="false"></InputNumber>
                    </div>
                    <div class="flex justify-between">
                      <div class="flex flex-col gap-1">
                        <p>Principal Bal.</p>
                        <span class="font-bold text-xl">{{ formatCurrency(principalBalance) }}</span>
                      </div>
                      <div class="flex flex-col gap-1">
                        <p>Funds Avail.</p>
                        <span class="font-bold text-xl" :class="{ 'text-red-500': fundsAvailable < 0 }">{{ formatCurrency(fundsAvailable) }}</span>
                      </div>
                    </div>
                  </div>
                </template>
              </Card>
            </div>
          </template>
        </Card>
        <ScrollPanel class="h-[75vh]">
          <Accordion>
            <UnitPanel
              v-for="(unit, index) in units"
              v-model:unit="units[index]"
              :index
              :entities="props.entities"
              :principal="principalPer"
            ></UnitPanel>
          </Accordion>
        </ScrollPanel>
      </template>
    </Card>
  </div>
</template>

<script setup lang="ts">
import type { Entity } from "@/types/Entity";
import type { Project } from "@/types/Projects";
import { formatCurrency } from "@/services/Utility";
import type { FlexRowField } from "@/types/FlexRows";
import type { ProjectEntity } from "@/types/ProjectEntity";

interface Props {
  entities: Entity[];
  project: Project;
  currentFlexFields: FlexRowField[];
  sourceOfFunds: ProjectEntity[];
}
const props = defineProps<Props>();

const salesPrice = computed(() => {
  const field: FlexRowField | undefined = props.currentFlexFields.find((field: FlexRowField) => field.label === "Sales Price");
  return field == null ? 0 : field.numericValue == null ? 0 : field.numericValue;
});

const commitment = ref(0);

const totalCommited = computed<number>(() => {
  return props.sourceOfFunds.reduce((sum, current) => sum + (current.type.toLowerCase().includes("lender") ? current.totalFundsAdjustment : 0), 0);
});

const totalAvailable = computed<number>(() => commitment.value - totalCommited.value);

const availableBalance = ref(0);

const totalPaydown = computed(() => units.value.reduce((sum, current) => sum + current.totalPaydown, 0));

const principalBalance = computed(() => props.project.projectDrawInfo.principalBalance - totalPaydown.value);
const fundsAvailable = computed(() => availableBalance.value - principalBalance.value);

const principalPer = computed(() => {
  if (units.value.length == 0) return 0;
  return props.project.projectDrawInfo.funds / units.value.length;
});

const units = ref([
  {
    name: "A101",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "A102",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "A103",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "A104",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "A105",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "B101",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "B102",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "B103",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "B104",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "B105",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "C101",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "C102",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "C103",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "C104",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "C105",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "D101",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "D102",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "D103",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "D104",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "D105",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "E101",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "E102",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "E103",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "E104",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "E105",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "F101",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "F102",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "F103",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "F104",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
  {
    name: "F105",
    buyer: "",
    deposit: 0,
    totalPaydown: 0,
    outstandingPrincipal: 0,
    sold: false,
    paydowns: [],
  },
]);

onBeforeMount(async (): Promise<void> => {});
</script>
